import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@utils/api';
import Header from '@components/Header';
import Footer from '@components/Footer';

// Cloudflare Turnstile site key
const TURNSTILE_SITE_KEY = '0x4AAAAAAB1lGkmkTK8Bar4P';
const TURNSTILE_ENABLED = true;

function Register() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [turnstileLoaded, setTurnstileLoaded] = useState(false);
    const turnstileRef = useRef(null);

    // Load Cloudflare Turnstile script
    useEffect(() => {
        if (!TURNSTILE_ENABLED) return;

        // Check if script already exists
        if (document.getElementById('turnstile-script')) {
            setTurnstileLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.id = 'turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            setTurnstileLoaded(true);
        };
        script.onerror = () => {
            setError('Failed to load security verification');
        };
        document.head.appendChild(script);

        return () => {
            // Cleanup
            const scriptEl = document.getElementById('turnstile-script');
            if (scriptEl) {
                scriptEl.remove();
            }
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('Password tidak cocok');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password minimal 6 karakter');
            return;
        }

        setLoading(true);

        try {
            // Get Turnstile token if enabled
            let turnstileToken = null;
            if (TURNSTILE_ENABLED && window.turnstile && turnstileRef.current) {
                try {
                    turnstileToken = window.turnstile.getResponse(turnstileRef.current);
                    if (!turnstileToken) {
                        setError('Please complete the security verification');
                        setLoading(false);
                        return;
                    }
                } catch (turnstileError) {
                    setError('Security verification error');
                    setLoading(false);
                    return;
                }
            }

            const response = await fetch(api('/api/auth/register'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: formData.username,
                    email: formData.email,
                    password: formData.password,
                    turnstileToken: turnstileToken
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Auto login after registration
                if (data.token) {
                    localStorage.setItem('userToken', data.token);
                    localStorage.setItem('userData', JSON.stringify(data.user));
                }
                navigate('/');
            } else {
                setError(data.message || 'Registrasi gagal');
                // Reset Turnstile
                if (TURNSTILE_ENABLED && window.turnstile && turnstileRef.current) {
                    window.turnstile.reset(turnstileRef.current);
                }
            }
        } catch (err) {
            setError('Terjadi kesalahan. Silakan coba lagi.');
            // Reset Turnstile
            if (TURNSTILE_ENABLED && window.turnstile && turnstileRef.current) {
                window.turnstile.reset(turnstileRef.current);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <Header />

            <main className="auth-container" style={{ maxWidth: '400px', margin: '120px auto', padding: '2rem' }}>
                <h2>Daftar Akun Baru</h2>

                {error && (
                    <div style={{ background: 'rgba(255, 107, 107, 0.1)', border: '1px solid #ff6b6b', borderRadius: '8px', padding: '12px', marginBottom: '1rem', color: '#ff6b6b' }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ccc' }}>Username</label>
                        <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            required
                            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)', color: '#fff' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ccc' }}>Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)', color: '#fff' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ccc' }}>Password</label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required
                            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)', color: '#fff' }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ccc' }}>Konfirmasi Password</label>
                        <input
                            type="password"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            required
                            style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.3)', color: '#fff' }}
                        />
                    </div>

                    {/* Cloudflare Turnstile Captcha */}
                    {TURNSTILE_ENABLED && (
                        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                            {turnstileLoaded ? (
                                <div
                                    ref={(el) => {
                                        if (el && !turnstileRef.current && window.turnstile) {
                                            try {
                                                turnstileRef.current = window.turnstile.render(el, {
                                                    sitekey: TURNSTILE_SITE_KEY,
                                                    theme: 'dark',
                                                    size: 'normal'
                                                });
                                            } catch (err) {
                                                // Silent fail
                                            }
                                        }
                                    }}
                                ></div>
                            ) : (
                                <div style={{ padding: '1rem', color: '#999', textAlign: 'center' }}>
                                    <i className="fas fa-spinner fa-spin"></i> Loading security verification...
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || (TURNSTILE_ENABLED && !turnstileLoaded)}
                        className="auth-btn primary"
                        style={{ width: '100%', padding: '0.75rem', marginTop: '1rem' }}
                    >
                        {loading ? 'Memproses...' : 'Daftar'}
                    </button>
                </form>

                <div style={{ marginTop: '1.5rem', textAlign: 'center', color: '#999' }}>
                    Sudah punya akun?{' '}
                    <Link to="/login" style={{ color: '#d4af37', textDecoration: 'none' }}>
                        Masuk
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default Register;
